import { Link } from "react-router-dom";
import { Zap, Gauge, Mountain, SprayCan, Disc3, Phone, ArrowUpRight } from "lucide-react";
import { Lightning } from "../components/Lightning";
import { FadeUp, MaskedLine, SectionHeading } from "../components/Reveal";
import { SERVICES, PHONE, PHONE_HREF, NEW_IMAGES } from "../site";

const PAINT_SHOTS = [
  { src: NEW_IMAGES.paintBadges, label: "F-450 Platinum Badges" },
  { src: NEW_IMAGES.paintHandle, label: "Door Handle" },
  { src: NEW_IMAGES.paintMirror, label: "Mirror Cap" },
  { src: NEW_IMAGES.paintRack, label: "Tail Lights & Trim" },
  { src: NEW_IMAGES.paintBooth, label: "Full Trim Kit" },
];

const SERVICE_ICONS = { diesel: Gauge, lifts: Mountain, lighting: Zap, wheels: Disc3, body: SprayCan };

const TAGS = {
  diesel: ["Diagnostics", "Turbo Upgrades", "Tuning"],
  lifts: ["Lift Kits", "Leveling", "Shocks"],
  lighting: ["Underglow", "Wheel Rings", "Headlights"],
  wheels: ["Deep Lips", "MT / AT Tires", "Fitment"],
  body: ["Paint Match", "Body Repair", "Trim & Badges"],
};

const ServiceRow = ({ service, index }) => {
  const Icon = SERVICE_ICONS[service.id];
  const flip = index % 2 === 1;
  return (
    <FadeUp>
      <div
        data-testid={`service-card-${service.id}`}
        className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center border-t border-zinc-800 py-14 lg:py-20"
      >
        <div className={`lg:col-span-2 ${flip ? "lg:order-3 lg:text-right" : ""}`}>
          <span className="font-display text-7xl lg:text-9xl font-black italic leading-none text-outline-volt transition-all duration-500 group-hover:text-volt group-hover:[-webkit-text-stroke:0px]">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
          <div className="mb-4 flex h-11 w-11 items-center justify-center clip-badge bg-bolt/10 border border-bolt/40 text-bolt-bright">
            <Icon size={20} strokeWidth={2.2} />
          </div>
          <h3 className="font-display text-3xl sm:text-5xl font-black uppercase italic tracking-tight text-white mb-4">
            {service.title}
          </h3>
          <p className="text-sm sm:text-base leading-relaxed text-zinc-400 mb-5 max-w-md">{service.desc}</p>
          <div className="mb-6 flex flex-wrap gap-2">
            {TAGS[service.id].map((t) => (
              <span key={t} className="clip-badge border border-zinc-700 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {t}
              </span>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid={`service-card-${service.id}-cta`}
            className="inline-flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-volt hover:text-volt-soft transition-colors"
          >
            Get a quote <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
          {service.image ? (
            <div className="clip-corner relative aspect-[4/3] overflow-hidden border border-zinc-800">
              <img
                src={service.image}
                alt={service.title}
                loading="lazy"
                className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {service.caption && (
                <p
                  data-testid={`service-card-${service.id}-caption`}
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-5 pb-4 pt-10 text-xs sm:text-sm leading-relaxed text-zinc-200"
                >
                  {service.caption}
                </p>
              )}
            </div>
          ) : (
            <div className="clip-corner relative flex aspect-[4/3] items-center justify-center overflow-hidden border border-zinc-800 bg-carbon">
              <Icon size={170} strokeWidth={0.4} className="text-zinc-800 transition-colors duration-700 group-hover:text-bolt/30" />
              <span className="absolute bottom-5 left-5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">
                ETN Customz · East TN
              </span>
            </div>
          )}
        </div>
      </div>
    </FadeUp>
  );
};

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="relative overflow-hidden pt-36 sm:pt-44 pb-14 sm:pb-16">
        <Lightning className="pointer-events-none absolute inset-0 h-full w-full opacity-60" interval={[3000, 6500]} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-volt mb-4">
            The Service Index
          </p>
          <h1 className="font-display font-black uppercase italic tracking-tighter leading-[0.85]" data-testid="services-headline">
            <MaskedLine delay={0.15} className="text-5xl sm:text-7xl lg:text-8xl text-white">
              Five ways we
            </MaskedLine>
            <MaskedLine delay={0.3} className="text-5xl sm:text-7xl lg:text-8xl text-outline-bolt">
              Transform trucks
            </MaskedLine>
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-zinc-400">
              Diesel diagnostics to full show-truck transformations — scroll the index. Every line is work we do in-house in Eastern Tennessee.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-20" data-testid="services-grid">
        {SERVICES.map((s, i) => (
          <ServiceRow key={s.id} service={s} index={i} />
        ))}
        <div className="border-t border-zinc-800" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-20 sm:pb-28" data-testid="paint-match-section">
        <SectionHeading
          eyebrow="Paint Match / Body Work"
          title={<>Fresh out<br /><span className="text-outline-bolt">the booth</span></>}
          className="mb-12"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {PAINT_SHOTS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.07}>
              <div
                data-testid={`paint-shot-${i}`}
                className="clip-corner hud-frame group relative aspect-[3/4] overflow-hidden border border-zinc-800"
              >
                <img
                  src={s.src}
                  alt={s.label}
                  loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-volt">
                  {s.label}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden">
        <img src={SERVICES[2].image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 py-24 sm:py-32 text-center">
          <FadeUp>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-bolt-bright mb-5">
              Not sure which line your build falls on?
            </p>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] text-white">
              Call the shop.<br /><span className="text-volt glow-volt">Talk it through.</span>
            </h2>
            <a
              href={PHONE_HREF}
              data-testid="services-call-button"
              className="clip-badge mt-10 inline-flex items-center gap-3 bg-volt px-10 py-5 font-display text-2xl font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:bg-volt-soft"
            >
              <Phone size={22} /> {PHONE}
            </a>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
